import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Users, User, Trophy, Info } from 'lucide-react';

import { useIsMobile } from "@/hooks/use-mobile";
import { FriendSelector } from './FriendSelector';
import { useUserProfile } from '@/hooks/useUserProfile';
import { useAllSubcategoryCounts } from "@/hooks/useAllSubcategoryCounts";
import { getSubcategoryMaxParticipants } from "@/utils/eventUtils";

interface RegistrationDataStepProps {
  event: any;
  data: any;
  onDataChange: (data: any) => void;
  onNext: () => void;
  onPrevious: (() => void) | null;
  isInvitationFlow?: boolean;
}

export const RegistrationDataStep = ({ event, data, onDataChange, onNext, onPrevious, isInvitationFlow = false }: RegistrationDataStepProps) => {
  const isMobile = useIsMobile();
  const { profile } = useUserProfile();
  const [selectedModality, setSelectedModality] = useState(data.modality || '');
  const [selectedCategory, setSelectedCategory] = useState(data.category || '');
  const [selectedSubcategory, setSelectedSubcategory] = useState(data.subcategory || '');
  const [player1Name, setPlayer1Name] = useState(data.player1Name || profile?.full_name || '');
  const [player2Name, setPlayer2Name] = useState(data.player2Name || '');
  
  // New partner selection states
  const [partnerSelectionMethod, setPartnerSelectionMethod] = useState(data.partnerSelectionMethod || '');
  const [selectedFriend, setSelectedFriend] = useState(data.selectedFriend || null);
  
  // Determine available modalities
  const getAvailableModalities = () => {
    if (event.sport?.toLowerCase().includes('pádel') || event.sport?.toLowerCase().includes('padel')) {
      if (event.event_type === 'league') {
        return event.match_type === 'ambos' ? ['singles', 'dobles'] : [event.match_type];
      }
      return ['dobles']; // Padel tournaments are always doubles
    }
    return event.match_type === 'ambos' ? ['singles', 'dobles'] : [event.match_type];
  };

  const availableModalities = getAvailableModalities();
  const isDoublesRequired = selectedModality === 'dobles';

  // Auto-select modality when only one is available
  useEffect(() => {
    if (availableModalities.length === 1 && !selectedModality) {
      setSelectedModality(availableModalities[0]);
    }
  }, [availableModalities, selectedModality]);

  // Auto-select category when only one is available (leagues typically have 1 category)
  useEffect(() => {
    if (event.genderCategories?.length === 1 && !selectedCategory) {
      setSelectedCategory(event.genderCategories[0]);
    } else if (event.gender_category && (!event.genderCategories || event.genderCategories.length === 0) && !selectedCategory) {
      // For leagues with single category: auto-select from gender_category field
      setSelectedCategory(event.gender_category);
    }
  }, [event.genderCategories, event.gender_category, selectedCategory]);

  // Get available subcategories based on selected category
  const getAvailableSubcategories = () => {
    if (!selectedCategory || !event.subcategories) return [];
    
    // event.subcategories is an object where keys are gender categories
    // and values are arrays of subcategories for that gender
    return event.subcategories[selectedCategory] || [];
  };

  const availableSubcategories = getAvailableSubcategories();

  // Get participant counts for ALL subcategories at once
  const { counts: subcategoryCounts, loading: allSubcategoriesLoading } = useAllSubcategoryCounts(
    event.id.toString(),
    selectedCategory,
    availableSubcategories,
    event.rules
  );

  // Get counts for currently selected subcategory (for validation)
  const selectedSubcategoryInfo = subcategoryCounts[selectedSubcategory];
  const pairsCount = selectedSubcategoryInfo?.pairsCount || 0;
  const subcategoryMax = selectedSubcategoryInfo?.max || null;
  const remainingPairs = selectedSubcategoryInfo?.remaining || null;

  // Handle category change and auto-select subcategory (combined to avoid race condition)
  useEffect(() => {
    // If only one subcategory is available, auto-select it
    if (availableSubcategories.length === 1) {
      setSelectedSubcategory(availableSubcategories[0]);
    } else if (availableSubcategories.length === 0 || !availableSubcategories.includes(selectedSubcategory)) {
      // Reset only if current selection is invalid for the new category
      setSelectedSubcategory('');
    }
  }, [selectedCategory, availableSubcategories]);

  // Initialize form data from props
  useEffect(() => {
    if (data.modality) {
      setSelectedModality(data.modality);
    }
    if (data.category) {
      setSelectedCategory(data.category);
    }
    if (data.subcategory) {
      setSelectedSubcategory(data.subcategory);
    }
    if (data.player1Name) {
      setPlayer1Name(data.player1Name);
    }
    if (data.player2Name) {
      setPlayer2Name(data.player2Name);
    }
    if (data.partnerSelectionMethod) {
      setPartnerSelectionMethod(data.partnerSelectionMethod);
    }
    if (data.selectedFriend) {
      setSelectedFriend(data.selectedFriend);
    }
  }, [data]);

  // Auto-fill player1Name with user's full name when profile loads
  useEffect(() => {
    if (profile?.full_name && !data.player1Name && !player1Name) {
      setPlayer1Name(profile.full_name);
    }
  }, [profile?.full_name, data.player1Name, player1Name]);

  // Handle form validation
  const isFormValid = () => {
    if (!selectedModality || !selectedCategory) return false;
    if (!player1Name.trim()) return false;
    
    // Check if subcategory is full (use !== null to handle 0 correctly)
    if (subcategoryMax !== null && pairsCount >= subcategoryMax) {
      return false;
    }
    
    if (isDoublesRequired) {
      if (!partnerSelectionMethod) return false;
      
      if (partnerSelectionMethod === 'manual') {
        return player2Name.trim() !== '';
      } else if (partnerSelectionMethod === 'friend') {
        return selectedFriend !== null;
      }
    }
    
    return true;
  };

  // Calculate payment amount - entry_fee already represents total amount
  // For doubles: price per pair (e.g., $1,800)
  // For singles: price per person (e.g., $900)
  const calculatePaymentAmount = () => {
    return event.entry_fee || 0;
  };

  const handleContinueToPayment = () => {
    if (!isFormValid()) return;

    // Save form data and go to step 2 (payment summary)
    const formData = {
      modality: selectedModality,
      category: selectedCategory,
      subcategory: selectedSubcategory,
      player1Name: player1Name.trim(),
      player2Name: player2Name.trim(),
      partnerSelectionMethod,
      selectedFriend,
      paymentMode: 'full_amount',
    };

    onDataChange(formData);
    localStorage.setItem('pendingRegistrationData', JSON.stringify(formData));
    onNext();
  };

  return (
    <div className={`${isMobile ? 'space-y-4' : 'space-y-6'}`}>
      {/* Header - Optimized for mobile */}
      <div className={`text-center ${isMobile ? 'mb-4' : 'mb-6'}`}>
        <h2 className={`${isMobile ? 'text-xl' : 'text-2xl'} font-bold text-gray-900 mb-2`}>
          Datos de Inscripción
        </h2>
        <p className={`text-gray-600 ${isMobile ? 'text-sm' : 'text-lg'}`}>
          Completa la información para inscribirte al {event.event_type === 'league' ? 'liga' : 'torneo'}
        </p>
      </div>

      {/* Modality Selection - Show even with single modality */}
      <Card className={isMobile ? 'shadow-sm' : ''}>
        <CardHeader className={isMobile ? 'pb-3 px-4 pt-4' : ''}>
          <CardTitle className={`flex items-center gap-2 ${isMobile ? 'text-base' : ''}`}>
            <Users className={`${isMobile ? 'h-4 w-4' : 'h-5 w-5'}`} />
            Modalidad
          </CardTitle>
        </CardHeader>
        <CardContent className={isMobile ? 'px-4 pb-4' : ''}>
          {availableModalities.length === 1 ? (
            <div className="space-y-3">
              <div className={`flex items-center gap-3 p-3 bg-primary/5 border border-primary/20 rounded-lg ${isMobile ? 'p-2' : ''}`}>
                {availableModalities[0] === 'singles' ? (
                  <>
                    <User className={`${isMobile ? 'h-4 w-4' : 'h-5 w-5'} text-primary`} />
                    <div>
                      <div className={`font-medium text-primary ${isMobile ? 'text-sm' : ''}`}>
                        Singles (Individual)
                      </div>
                      <div className={`text-primary/80 ${isMobile ? 'text-xs' : 'text-sm'}`}>
                        Este {event.event_type === 'league' ? 'liga' : 'torneo'} es solo individual
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <Users className={`${isMobile ? 'h-4 w-4' : 'h-5 w-5'} text-primary`} />
                    <div>
                      <div className={`font-medium text-primary ${isMobile ? 'text-sm' : ''}`}>
                        Dobles (Parejas)
                      </div>
                      <div className={`text-primary/80 ${isMobile ? 'text-xs' : 'text-sm'}`}>
                        Este {event.event_type === 'league' ? 'liga' : 'torneo'} es solo de parejas, necesitarás un compañero
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          ) : (
            <RadioGroup value={selectedModality} onValueChange={setSelectedModality}>
              <div className={`${isMobile ? 'space-y-3' : 'space-y-2'}`}>
                {availableModalities.map((modality) => (
                  <div key={modality} className={`flex items-center space-x-2 ${isMobile ? 'py-1' : ''}`}>
                    <RadioGroupItem value={modality} id={modality} className={isMobile ? 'h-4 w-4' : ''} />
                    <Label htmlFor={modality} className={`flex items-center gap-2 cursor-pointer ${isMobile ? 'text-sm' : ''}`}>
                      {modality === 'singles' ? (
                        <>
                          <User className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4'}`} />
                          Singles (Individual)
                        </>
                      ) : (
                        <>
                          <Users className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4'}`} />
                          Dobles (Parejas)
                        </>
                      )}
                    </Label>
                  </div>
                ))}
              </div>
            </RadioGroup>
          )}
        </CardContent>
      </Card>

      {/* Category Selection - Compact for mobile */}
      <Card className={isMobile ? 'shadow-sm' : ''}>
        <CardHeader className={isMobile ? 'pb-3 px-4 pt-4' : ''}>
          <CardTitle className={`flex items-center gap-2 ${isMobile ? 'text-base' : ''}`}>
            <Trophy className={`${isMobile ? 'h-4 w-4' : 'h-5 w-5'}`} />
            Categoría
          </CardTitle>
        </CardHeader>
        <CardContent className={isMobile ? 'px-4 pb-4' : ''}>
          {(event.genderCategories?.length === 1 || 
            (event.gender_category && (!event.genderCategories || event.genderCategories.length === 0))) ? (
            // Single category - Show as informative card (not selectable)
            <div className={`flex items-center gap-3 p-3 bg-primary/5 border border-primary/20 rounded-lg ${isMobile ? 'p-2' : ''}`}>
              <Trophy className={`${isMobile ? 'h-4 w-4' : 'h-5 w-5'} text-primary`} />
              <div>
                <div className={`font-medium text-primary ${isMobile ? 'text-sm' : ''}`}>
                  {event.genderCategories?.[0] || event.gender_category || event.category}
                </div>
                <div className={`text-primary/80 ${isMobile ? 'text-xs' : 'text-sm'}`}>
                  Categoría de esta {event.event_type === 'league' ? 'liga' : 'torneo'}
                </div>
              </div>
            </div>
          ) : (
            // Multiple categories - Show dropdown selector
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className={isMobile ? 'h-10 text-sm' : ''}>
                <SelectValue placeholder="Selecciona una categoría" />
              </SelectTrigger>
              <SelectContent className="z-50 bg-white">
                {event.genderCategories?.map((category: string) => (
                  <SelectItem key={category} value={category} className={isMobile ? 'text-sm py-2' : ''}>
                    {category}
                  </SelectItem>
                )) || event.gender_category && (
                  <SelectItem key={event.gender_category} value={event.gender_category}>
                    {event.gender_category}
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
          )}

        </CardContent>
      </Card>

      {/* Subcategory Selection - Independent Card */}
      {selectedCategory && availableSubcategories.length > 1 && (
        <Card className={isMobile ? 'shadow-sm' : ''}>
          <CardHeader className={isMobile ? 'pb-3 px-4 pt-4' : ''}>
            <CardTitle className={`flex items-center gap-2 ${isMobile ? 'text-base' : ''}`}>
              <Trophy className={`${isMobile ? 'h-4 w-4' : 'h-5 w-5'}`} />
              Subcategoría
            </CardTitle>
          </CardHeader>
          <CardContent className={isMobile ? 'px-4 pb-4' : ''}>
            {availableSubcategories.length === 1 ? (
              // Single subcategory - Show as informative card (not selectable)
              <div className={`flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg ${isMobile ? 'p-2' : ''}`}>
                <Trophy className={`${isMobile ? 'h-4 w-4' : 'h-5 w-5'} text-green-600`} />
                <div>
                  <div className={`font-medium text-green-900 ${isMobile ? 'text-sm' : ''}`}>
                    {availableSubcategories[0]}
                  </div>
                  <div className={`text-green-700 ${isMobile ? 'text-xs' : 'text-sm'}`}>
                    Subcategoría de esta {event.event_type === 'league' ? 'liga' : 'torneo'}
                  </div>
                </div>
              </div>
            ) : (
              // Multiple subcategories - Show dropdown with availability info
              <>
                <Select value={selectedSubcategory} onValueChange={(value) => {
                  // Only allow selection if subcategory is not full
                  const info = subcategoryCounts[value];
                  if (!info?.isFull) {
                    setSelectedSubcategory(value);
                  }
                }}>
                  <SelectTrigger className={isMobile ? 'h-10 text-sm' : ''}>
                    <SelectValue placeholder="Selecciona una subcategoría" />
                  </SelectTrigger>
                  <SelectContent className="z-50 bg-white">
                    {availableSubcategories.map((subcategory: string) => {
                      const info = subcategoryCounts[subcategory];
                      const isFull = info?.isFull || false;
                      const currentPairs = info?.pairsCount || 0;
                      const max = info?.max;
                      const remaining = info?.remaining;
                      
                      return (
                        <SelectItem 
                          key={subcategory} 
                          value={subcategory}
                          disabled={isFull}
                          className={`${isMobile ? 'text-sm py-3' : 'py-3'} ${
                            isFull ? 'opacity-50 cursor-not-allowed' : ''
                          } data-[state=checked]:bg-green-500 data-[state=checked]:text-white`}
                        >
                          <div className="flex items-center gap-2 w-full">
                            <span className={`font-semibold ${isFull ? 'line-through' : ''}`}>
                              {subcategory}
                            </span>
                            {max !== null ? (
                              <Badge 
                                variant={isFull ? "destructive" : "default"}
                                className={`ml-auto text-xs px-2 py-0.5 ${
                                  isFull 
                                    ? 'bg-red-100 text-red-700 hover:bg-red-100' 
                                    : 'bg-green-500 text-white hover:bg-green-500'
                                }`}
                              >
                                {isFull ? 'LLENO' : `${remaining} disponibles`}
                              </Badge>
                            ) : (
                              <Badge className="ml-auto text-xs px-2 py-0.5 bg-blue-100 text-blue-700 hover:bg-blue-100">
                                {currentPairs} {currentPairs === 1 ? 'inscrito' : 'inscritos'}
                              </Badge>
                            )}
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                
                {/* Selected subcategory summary */}
                {selectedSubcategory && subcategoryMax !== null && (
                  <div className={`mt-3 text-sm ${
                    remainingPairs !== null && remainingPairs <= 3 
                      ? 'text-orange-600 font-medium' 
                      : 'text-muted-foreground'
                  }`}>
                    {allSubcategoriesLoading ? (
                      'Cargando disponibilidad...'
                    ) : remainingPairs !== null ? (
                      remainingPairs > 0 ? (
                        <>
                          Cupos disponibles en {selectedCategory} {selectedSubcategory}:{' '}
                          <strong>{remainingPairs} {remainingPairs === 1 ? 'pareja' : 'parejas'}</strong>
                          {remainingPairs <= 3 && ' ⚠️ Últimos cupos'}
                        </>
                      ) : (
                        <span className="text-destructive font-medium">
                          ❌ Cupos agotados en {selectedCategory} {selectedSubcategory}
                        </span>
                      )
                    ) : null}
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Player Names - Compact for mobile */}
      <Card className={isMobile ? 'shadow-sm' : ''}>
        <CardHeader className={isMobile ? 'pb-3 px-4 pt-4' : ''}>
          <CardTitle className={`flex items-center gap-2 ${isMobile ? 'text-base' : ''}`}>
            <User className={`${isMobile ? 'h-4 w-4' : 'h-5 w-5'}`} />
            {isDoublesRequired ? 'Nombres de los Jugadores' : 'Nombre del Jugador'}
          </CardTitle>
        </CardHeader>
        <CardContent className={`${isMobile ? 'px-4 pb-4 space-y-3' : 'space-y-4'}`}>
          <div>
            <Label htmlFor="player1" className={isMobile ? 'text-xs font-medium' : ''}>
              {isDoublesRequired ? 'Jugador 1' : 'Nombre completo'}
            </Label>
            <Input
              id="player1"
              value={player1Name}
              onChange={(e) => setPlayer1Name(e.target.value)}
              placeholder="Ingresa tu nombre completo"
              className={`mt-1 ${isMobile ? 'h-10 text-sm' : ''}`}
            />
          </div>

          {isDoublesRequired && (
            <>
              {/* Partner Selection Method */}
              <div className={isMobile ? 'mt-3' : 'mt-4'}>
                <Label className={`${isMobile ? 'text-xs' : 'text-sm'} font-medium mb-2 block`}>
                  ¿Cómo quieres agregar a tu compañero?
                </Label>
                <RadioGroup value={partnerSelectionMethod} onValueChange={setPartnerSelectionMethod}>
                  <div className={`${isMobile ? 'space-y-2' : 'space-y-3'}`}>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="manual" id="manual" />
                      <Label htmlFor="manual" className="cursor-pointer text-sm">
                        Ingresar nombre manualmente
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="friend" id="friend" />
                      <Label htmlFor="friend" className="cursor-pointer text-sm">
                        Seleccionar de mis amigos
                      </Label>
                    </div>
                  </div>
                </RadioGroup>
              </div>

              {/* Manual Partner Name Input */}
              {partnerSelectionMethod === 'manual' && (
                <div className={isMobile ? 'mt-3' : 'mt-4'}>
                  <Label htmlFor="player2" className={isMobile ? 'text-xs font-medium' : ''}>
                    Nombre del compañero/a
                  </Label>
                  <Input
                    id="player2"
                    value={player2Name}
                    onChange={(e) => setPlayer2Name(e.target.value)}
                    placeholder="Ingresa el nombre de tu compañero/a"
                    className={`mt-1 ${isMobile ? 'h-10 text-sm' : ''}`}
                  />
                  <p className={`text-xs text-muted-foreground mt-1 ${isMobile ? 'text-xs' : ''}`}>
                    Serás responsable de pagar la inscripción completa por ambos
                  </p>
                </div>
              )}

              {/* Friend Selection */}
              {partnerSelectionMethod === 'friend' && (
                <div className={isMobile ? 'mt-3' : 'mt-4'}>
                  <Label className={`${isMobile ? 'text-xs' : 'text-sm'} font-medium mb-2 block`}>
                    Buscar amigo
                  </Label>
                  <FriendSelector
                    onSelectFriend={setSelectedFriend}
                    selectedFriend={selectedFriend}
                    eventSport={event.sport || ''}
                  />
                </div>
              )}

              {/* Payment Information - When friend is selected */}
              {partnerSelectionMethod === 'friend' && selectedFriend && (
                <div className={isMobile ? 'mt-3' : 'mt-4'}>
                  <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                    <div className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0"></div>
                      <div className="text-sm text-primary/80">
                        <strong>Pago completo:</strong> Pagarás la inscripción completa por ti y por {selectedFriend?.full_name || selectedFriend?.email || 'tu compañero'}. 
                        Se le enviará una invitación para que confirme su participación.
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>


      {/* Continue Button - Mobile optimized */}
      <div className={`${isMobile ? 'flex gap-2 pt-2' : 'flex gap-3 pt-4'} border-t`}>
        {onPrevious && (
          <Button 
            onClick={onPrevious}
            variant="outline"
            className={`flex-1 ${isMobile ? 'h-11 text-sm' : ''}`}
          >
            Volver
          </Button>
        )}
        <Button
          onClick={handleContinueToPayment}
          disabled={!isFormValid() || (subcategoryMax !== null && pairsCount >= subcategoryMax)}
          className={`${onPrevious ? 'flex-1' : 'w-full'} bg-green-600 hover:bg-green-700 text-white ${isMobile ? 'h-11 text-sm font-medium' : ''}`}
        >
          {subcategoryMax && pairsCount >= subcategoryMax ? (
            'Cupos Agotados en esta Subcategoría'
          ) : (
            `Continuar - $${calculatePaymentAmount()}`
          )}
        </Button>
      </div>
    </div>
  );
};